import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBridgeClubAuth } from '../../../contexts/BridgeClubAuthContext';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { deliveryAPI } from '../../../services/deliveryAPI';
import AnimalManager from '../../../components/shared/AnimalManager';
import SetupBinsStep from './SetupBinsStep';
import DeliveryPreferencesStep from './DeliveryPreferencesStep';
import PropertyCaptureStep from './PropertyCaptureStep';
import CardStep from './CardStep';

type StepKey = 'animals' | 'bins' | 'preferences' | 'property' | 'card';

const RED = '#B91C1C';
const DASHBOARD = '/bridge-club/dashboard';

// Map the server's delivery next_step to a UI step (or the dashboard when nothing is left).
const stepForNext = (next: string | null): StepKey | 'dashboard' => {
  switch (next) {
    case 'delivery_preferences': return 'preferences';
    case 'property_capture': return 'property';
    case 'card': return 'card';
    case null: return 'dashboard';
    default: return 'preferences';
  }
};

/**
 * Guided setup every Bridge Club member completes after verifying their email: animals,
 * then bins. Required and resumable — the gate keeps an unfinished member here, and on
 * entry we resume at the first incomplete step from server state.
 *
 * Delivery subscribers have three more steps: 3 (preferences), 4 (property), and 5 (the
 * card — service tier + payment, which finalises the subscription). The universal
 * completion flag (member.onboarding_complete) is set at the end of bins for everyone; the
 * delivery steps resume from the delivery onboarding endpoint (GET /me/onboarding/ →
 * next_step).
 */
const SetupFlow: React.FC = () => {
  const { member, refreshMember } = useBridgeClubAuth();
  const navigate = useNavigate();

  const isDelivery = !!member?.is_delivery_subscriber;

  const [step, setStep] = useState<StepKey>('animals');
  const [animalsCount, setAnimalsCount] = useState(0);
  const [resuming, setResuming] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const bootstrapped = useRef(false);

  // Resume once on mount.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    (async () => {
      try {
        // Universal (animals + bins) not done yet → resume there.
        if (!member?.onboarding_complete) {
          const animals = await bridgeClubAPI.getAnimals();
          const nAnimals = Array.isArray(animals) ? animals.length : 0;
          setAnimalsCount(nAnimals);
          setStep(nAnimals === 0 ? 'animals' : 'bins');
          return;
        }
        // Universal done. A non-delivery member is finished → dashboard. A delivery member
        // resumes their delivery steps from server state.
        if (!isDelivery) {
          navigate(DASHBOARD, { replace: true });
          return;
        }
        const state = await deliveryAPI.getOnboarding();
        const target = stepForNext(state.next_step);
        if (target === 'dashboard') navigate(DASHBOARD, { replace: true });
        else setStep(target);
      } catch {
        setStep(member?.onboarding_complete ? 'preferences' : 'animals');
      } finally {
        setResuming(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // End of the universal (animals + bins) flow: set the shared flag, then continue into the
  // delivery steps for a delivery subscriber, or the dashboard for everyone else.
  const finalizeUniversal = async () => {
    setFinishing(true);
    try {
      await bridgeClubAPI.completeOnboarding();
      await refreshMember();
    } catch {
      // Even if the flag write hiccups, don't strand the member; the gate brings them back.
    } finally {
      setFinishing(false);
    }
    if (isDelivery) setStep('preferences');
    else navigate(DASHBOARD, { replace: true });
  };

  // COUNT FROM WHERE THEY ARE. A member who finished animals and bins months ago and is
  // now adding delivery has three steps in front of them, not "step 3 of 5" with two
  // invisible ones behind it. Frozen at mount: finishing the universal half mid-flow must
  // not renumber the steps under the customer.
  const [startedUniversalDone] = useState(() => !!member?.onboarding_complete);
  const flowSteps: StepKey[] =
    startedUniversalDone && isDelivery
      ? ['preferences', 'property', 'card']
      : isDelivery
        ? ['animals', 'bins', 'preferences', 'property', 'card']
        : ['animals', 'bins'];
  const totalSteps = flowSteps.length;
  const stepNumber = Math.max(1, flowSteps.indexOf(step) + 1);

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f7' }}>
      <header style={{ background: RED, color: '#fff', padding: '10px 16px', textAlign: 'center' }}>
        <img src="/rbf-logo.png" alt="Red Bridge Farm" style={{ height: 28 }} />
        <div style={{ fontSize: 13, marginTop: 2, opacity: 0.9 }}>
          Step {stepNumber} of {totalSteps}
        </div>
      </header>

      <main style={{ padding: '20px 0' }}>
        {resuming ? (
          <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>Loading your setup…</div>
        ) : step === 'animals' ? (
          <div style={{ maxWidth: 620, margin: '0 auto', padding: '0 16px' }}>
            <h2 style={{ color: RED, marginBottom: 4 }}>Add your animals</h2>
            <p style={{ color: '#555', marginTop: 0 }}>
              Tell us what you're feeding. You can add more any time.
            </p>
            <AnimalManager
              apiBase="/bridge-club"
              themeColor={RED}
              selectedButtonColor="#60A5FA"
              onAnimalsChange={(a) => setAnimalsCount(a.length)}
              onError={(e) => console.error('[Onboarding animals]', e)}
            />
            {/* PINNED (Brad, 2026-09-23: "step 1 runs out of the view and I have to
                scroll"). The animals area is tall even when empty; the way forward stays
                on screen at the bottom, above the phone's safe area. */}
            <div style={{ position: 'sticky', bottom: 0, background: '#f7f7f7', borderTop: '1px solid #e5e7eb', margin: '12px -16px 0', padding: '10px 16px calc(10px + env(safe-area-inset-bottom, 0))', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#b45309', fontSize: 13 }}>{animalsCount === 0 ? 'Add at least one animal to continue.' : ''}</span>
              <button
                onClick={() => setStep('bins')}
                disabled={animalsCount === 0}
                style={{ minHeight: 44, padding: '0 20px', border: 'none', borderRadius: 8, background: animalsCount === 0 ? '#9ca3af' : RED, color: '#fff', fontWeight: 700, cursor: animalsCount === 0 ? 'default' : 'pointer', flexShrink: 0 }}
              >
                Next: What they eat →
              </button>
            </div>
          </div>
        ) : step === 'bins' ? (
          <SetupBinsStep
            onBack={() => setStep('animals')}
            onFinish={finalizeUniversal}
            finishLabel={isDelivery ? 'Continue →' : 'Finish setup'}
            finishing={finishing}
          />
        ) : step === 'preferences' ? (
          <DeliveryPreferencesStep
            onBack={() => setStep('bins')}
            onNext={() => setStep('property')}
            onSkip={() => navigate(DASHBOARD, { replace: true })}
          />
        ) : step === 'property' ? (
          <PropertyCaptureStep
            onBack={() => setStep('preferences')}
            onDone={() => setStep('card')}
          />
        ) : (
          <CardStep
            onBack={() => setStep('property')}
            onDone={() => navigate(DASHBOARD, { replace: true })}
          />
        )}
      </main>
    </div>
  );
};

export default SetupFlow;
